import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { skipToken } from '@reduxjs/toolkit/query';
import { ArrowLeft, Search, TrendingUp, TrendingDown, Loader2, School } from 'lucide-react';
import {
  useGetNationalHolisticAnalyticsQuery,
  mergeNationalAnalyticsResponses,
  deriveRecordMetrics,
  parseTermFilter,
  termFilterToQueryValue,
  TermFilter,
  useGetHpcYearsQuery,
} from '../../../services/holisticApi';

const BASE = '/kvs-samagam/student-analytics';
const LIMIT_OPTIONS = [5, 10, 15, 20, 25];

// ── Helpers ──────────────────────────────────────────────────────

function competencyLevel(pct: number): string {
  if (pct >= 85) return 'Excellent';
  if (pct >= 70) return 'Good';
  if (pct >= 55) return 'Average';
  return 'Below Average';
}

function levelBadge(pct: number): string {
  if (pct >= 85) return 'bg-emerald-50 text-emerald-700 border border-emerald-200';
  if (pct >= 70) return 'bg-indigo-50 text-indigo-700 border border-indigo-200';
  if (pct >= 55) return 'bg-amber-50 text-amber-700 border border-amber-200';
  return 'bg-red-50 text-red-700 border border-red-200';
}

function competencyColor(pct: number): string {
  if (pct >= 85) return 'text-emerald-600 font-semibold';
  if (pct >= 70) return 'text-indigo-600 font-semibold';
  if (pct >= 55) return 'text-amber-600 font-semibold';
  return 'text-red-600 font-semibold';
}

// ── Row type derived from holistic records ────────────────────────

interface SchoolRow {
  schoolId: string;
  schoolName: string;
  regionId: number;
  roName: string;
  totalStudents: number;
  avgCompetency: number;
}

// ── SchoolTable component ─────────────────────────────────────────

interface SchoolTableProps {
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  headerColor: string;
  schools: SchoolRow[];
  isLoading: boolean;
  onRowClick: (school: SchoolRow) => void;
  search: string;
  onSearchChange: (v: string) => void;
}

const SchoolTable: React.FC<SchoolTableProps> = ({
  title, subtitle, icon, headerColor, schools, isLoading, onRowClick, search, onSearchChange,
}) => {
  const filtered = useMemo(() => {
    if (!search) return schools;
    const q = search.toLowerCase();
    return schools.filter(
      s => s.schoolName.toLowerCase().includes(q) || s.roName.toLowerCase().includes(q),
    );
  }, [schools, search]);

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden flex flex-col">
      <div className={`px-5 py-4 border-b border-gray-100 ${headerColor}`}>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            {icon}
            <div>
              <h2 className="text-base font-bold text-gray-900">{title}</h2>
              <p className="text-xs text-gray-500">{subtitle}</p>
            </div>
          </div>
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={search}
              onChange={e => onSearchChange(e.target.value)}
              placeholder="Search schools..."
              className="pl-8 pr-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-200 w-44 bg-white"
            />
          </div>
        </div>
      </div>

      <div className="overflow-x-auto flex-1">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-indigo-400" />
            <span className="ml-2 text-sm text-gray-400">Loading…</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <School size={32} className="mb-2 opacity-40" />
            <p className="text-sm">No schools found</p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase w-10">#</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">School</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Region</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Students</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Avg Score</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Level</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.map((s, idx) => (
                <tr
                  key={s.schoolId}
                  onClick={() => onRowClick(s)}
                  className="hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <td className="px-4 py-3 text-xs text-gray-400 font-medium">{idx + 1}</td>
                  <td className="px-4 py-3">
                    <p className="text-sm font-semibold text-gray-900 leading-tight">{s.schoolName}</p>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">{s.roName}</td>
                  <td className="px-4 py-3 text-right text-sm text-gray-700">
                    {s.totalStudents.toLocaleString()}
                  </td>
                  <td className={`px-4 py-3 text-right text-sm ${competencyColor(s.avgCompetency)}`}>
                    {s.avgCompetency.toFixed(1)}%
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${levelBadge(s.avgCompetency)}`}>
                      {competencyLevel(s.avgCompetency)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

// ── Main screen ───────────────────────────────────────────────────

const FoundationSchoolPerformanceScreen: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: yearsRes } = useGetHpcYearsQuery();
  const availableYears = useMemo(() => {
    return yearsRes?.data || [2026, 2025];
  }, [yearsRes]);

  const term: TermFilter = parseTermFilter(searchParams.get('term'));
  const yearRaw = Number(searchParams.get('year'));
  const academicYear = Number.isFinite(yearRaw) && yearRaw > 2000 ? yearRaw : 2025;
  const limitRaw = Number(searchParams.get('limit'));
  const limit = Number.isFinite(limitRaw) && limitRaw > 0 ? limitRaw : 10;

  const [topSearch, setTopSearch] = useState('');
  const [bottomSearch, setBottomSearch] = useState('');

  const isOverall = term === 'overall';

  const singleQuery = useGetNationalHolisticAnalyticsQuery(
    !isOverall ? { academicYear, term } : skipToken,
  );
  const term1Query = useGetNationalHolisticAnalyticsQuery(
    isOverall ? { academicYear, term: 1 } : skipToken,
  );
  const term2Query = useGetNationalHolisticAnalyticsQuery(
    isOverall ? { academicYear, term: 2 } : skipToken,
  );

  const isLoading = isOverall
    ? (term1Query.isLoading || term2Query.isLoading)
    : singleQuery.isLoading;

  const effectiveData = useMemo(() => {
    if (!isOverall) return singleQuery.data;
    return mergeNationalAnalyticsResponses(term1Query.data, term2Query.data);
  }, [isOverall, singleQuery.data, term1Query.data, term2Query.data]);

  // Derive all schools from all region records
  const allSchools: SchoolRow[] = useMemo(() => {
    if (!effectiveData?.data?.length) return [];

    const schoolMap = new Map<number, {
      schoolName: string;
      regionId: number;
      roName: string;
      competencySum: number;
      count: number;
    }>();

    effectiveData.data.forEach(regionPayload => {
      (regionPayload.records || []).forEach(record => {
        const schoolId = Number(record.school_id ?? record.schoolID ?? record.kvID ?? -1);
        if (!Number.isFinite(schoolId) || schoolId < 0) return;

        const competency = deriveRecordMetrics(record, term).competency;
        const existing = schoolMap.get(schoolId);

        if (existing) {
          existing.competencySum += competency;
          existing.count += 1;
        } else {
          schoolMap.set(schoolId, {
            schoolName: record.schoolName || `School ${schoolId}`,
            regionId: regionPayload.region_id,
            roName: regionPayload.roName,
            competencySum: competency,
            count: 1,
          });
        }
      });
    });

    return Array.from(schoolMap.entries()).map(([schoolId, s]) => ({
      schoolId: String(schoolId),
      schoolName: s.schoolName,
      regionId: s.regionId,
      roName: s.roName,
      totalStudents: s.count,
      avgCompetency: +(s.competencySum / Math.max(1, s.count)).toFixed(1),
    }));
  }, [effectiveData]);

  const topSchools = useMemo(
    () => [...allSchools].sort((a, b) => b.avgCompetency - a.avgCompetency).slice(0, limit),
    [allSchools, limit],
  );
  const bottomSchools = useMemo(
    () => [...allSchools].sort((a, b) => a.avgCompetency - b.avgCompetency).slice(0, limit),
    [allSchools, limit],
  );

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams.toString());
    next.set(key, value);
    setSearchParams(next);
  };



  const querySuffix = `?year=${academicYear}&term=${termFilterToQueryValue(term)}`;
  const termLabel = term === 'overall' ? 'Overall' : `Term ${term}`;

  const handleSchoolClick = (s: SchoolRow) => {
    navigate(`${BASE}/region/${s.regionId}/school/${s.schoolId}${querySuffix}`);
  };

  return (
    <div className="p-6 max-w-[1600px] mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-start gap-3">
        <button
          onClick={() => navigate(-1)}
          className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 mt-0.5"
        >
          <ArrowLeft size={20} />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">School Performance Overview</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Showing top and bottom {limit} schools — Academic Year {academicYear}-{String(Number(academicYear) + 1).slice(-2)} — {termLabel}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
          <span className="text-xs font-medium text-gray-600">Show</span>
          <div className="flex items-center gap-1">
            {LIMIT_OPTIONS.map(n => (
              <button
                key={n}
                onClick={() => updateParam('limit', String(n))}
                className={`min-w-[2rem] text-xs font-medium px-2 py-1 rounded-md transition ${
                  limit === n ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {n}
              </button>
            ))}
          </div>
          <span className="text-xs text-gray-500">schools</span>
        </div>

        <div className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
          <label htmlFor="termSelect" className="text-xs font-medium text-gray-600">Term</label>
          <select
            id="termSelect"
            value={termFilterToQueryValue(term)}
            onChange={e => updateParam('term', e.target.value)}
            className="text-sm text-gray-700 bg-transparent focus:outline-none"
          >
            <option value="overall">Overall</option>
            <option value="1">Term 1</option>
            <option value="2">Term 2</option>
          </select>
        </div>

        <div className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
          <label htmlFor="yearSelect" className="text-xs font-medium text-gray-600">Academic Session</label>
          <select
            id="yearSelect"
            value={academicYear}
            onChange={e => updateParam('year', e.target.value)}
            className="text-sm text-gray-700 bg-transparent focus:outline-none"
          >
            {availableYears.map((y: number) => (
              <option key={y} value={y}>
                {y}-{String(Number(y) + 1).slice(-2)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Summary chips */}
      <div className="flex flex-wrap gap-3">
        <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-2">
          <TrendingUp size={16} className="text-emerald-600" />
          <span className="text-sm font-semibold text-emerald-700">Top Performing</span>
          <span className="text-sm text-emerald-600">— {topSchools.length} schools</span>
        </div>
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-4 py-2">
          <TrendingDown size={16} className="text-red-600" />
          <span className="text-sm font-semibold text-red-700">Need Attention</span>
          <span className="text-sm text-red-600">— {bottomSchools.length} schools</span>
        </div>
      </div>

      {/* Tables */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <SchoolTable
          title={`Top ${limit} Schools`}
          subtitle="Highest avg competency — click to drill down"
          icon={<TrendingUp size={18} className="text-emerald-600" />}
          headerColor="bg-emerald-50"
          schools={topSchools}
          isLoading={isLoading}
          onRowClick={handleSchoolClick}
          search={topSearch}
          onSearchChange={setTopSearch}
        />
        <SchoolTable
          title={`Bottom ${limit} Schools`}
          subtitle="Lowest avg competency — need immediate attention"
          icon={<TrendingDown size={18} className="text-red-500" />}
          headerColor="bg-red-50"
          schools={bottomSchools}
          isLoading={isLoading}
          onRowClick={handleSchoolClick}
          search={bottomSearch}
          onSearchChange={setBottomSearch}
        />
      </div>
    </div>
  );
};

export default FoundationSchoolPerformanceScreen;
